/*
 * Lemonade Training Oy copy rights all right reserved.
 * F032 step 2b: the dashboard Today page's server reads.
 */

/* ----------------------- Active member history ----------------------- */

/** Subcollection under Boxes/{boxID}; server-written, read by the gym's admins. */
export const BOX_STATS_COLLECTION = "Stats";

/**
 * One document per gym per calendar month (gym time zone). `days` maps the
 * two-digit day of month to that day's count of active members. The recorder
 * runs once a day at 23:45 Europe/Helsinki and is retried when it fails; a
 * retry overwrites the same day's entry. A whole missed day stays a gap, never a guess.
 */
export type ActiveMemberHistoryDoc = {
  /** "YYYY-MM" in the gym's time zone. */
  month: string;
  /** The zone the day keys were computed in. */
  timeZone: string;
  days: Record<string, number>;
  updatedAt: unknown;
};

export const activeMemberHistoryDocId = (month: string): string =>
  `activeMembers-${month}`;

/** "YYYY-MM" for a gym-local year and month (1-12). */
export const activeMemberMonthKey = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, "0")}`;

/** "01".."31" */
export const activeMemberDayKey = (day: number): string =>
  String(day).padStart(2, "0");

/* ---------------------------- Check-ins ------------------------------ */

/** Days without a class before someone counts as "back". Owner decision 2026-09-26. */
export const CHECK_IN_BACK_AFTER_DAYS = 14;

/** Most people one call looks at; the rest are reported as `capped`. */
export const TODAY_CHECK_INS_MAX_PEOPLE = 150;

export const TODAY_CHECK_IN_REASONS = ["first_class", "back_after"] as const;
export type TodayCheckInReason = (typeof TODAY_CHECK_IN_REASONS)[number];

export const isTodayCheckInReason = (
  value: unknown,
): value is TodayCheckInReason =>
  typeof value === "string" &&
  (TODAY_CHECK_IN_REASONS as readonly string[]).includes(value);

export type GetTodayCheckInsRequest = { boxID: string };

export type TodayCheckInPerson = {
  uid: string;
  /** The name on the booking; "" when the booking has none. */
  name: string;
  classID: string;
  /** ISO 8601 instant. */
  classStart: string;
  className: string;
  /** The class's coach; null when none is set. The coach's own view does not show it. */
  coachName: string | null;
  reason: TodayCheckInReason;
  /** Whole days since their previous class; null for a first class. */
  daysAway: number | null;
};

export type GetTodayCheckInsResponse = {
  /** "mine" = only the caller's classes (a coach); "all" = every class today (owner or admin). */
  scope: "mine" | "all";
  /** Ordered by class start, then name. */
  people: TodayCheckInPerson[];
  /** True when more people were booked than TODAY_CHECK_INS_MAX_PEOPLE; the list covers the first ones by class start. */
  capped: boolean;
};

/* ------------------------- Payment problems -------------------------- */

/** Names returned with the count. */
export const PAYMENT_PROBLEMS_PREVIEW = 5;

/** Membership rows read at most; above it the count is a floor and `capped` is true. */
export const PAYMENT_PROBLEMS_MAX_ROWS = 200;

export const PAYMENT_PROBLEM_STATES = ["grace", "paused"] as const;
export type PaymentProblemState = (typeof PAYMENT_PROBLEM_STATES)[number];

export type GetPaymentProblemsRequest = { boxID: string };

export type PaymentProblemPerson = {
  uid: string;
  name: string;
  membershipName: string;
  /** "paused" wins when one person has several memberships in trouble. */
  state: PaymentProblemState;
};

export type GetPaymentProblemsResponse = {
  /** People (not memberships) with at least one membership in grace or paused for non-payment. */
  count: number;
  capped: boolean;
  /** Up to PAYMENT_PROBLEMS_PREVIEW people, paused first; shown sorted by name within each state. */
  people: PaymentProblemPerson[];
};
